import PromptListItem from "@/components/prompt/prompt-list-item";
import PromptTriggerButton from "@/components/prompt/prompt-trigger-button";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useFavoritePrompts } from "@/hooks/usePromptsFavorite";
import { useLocalStorage } from "@/hooks/useStorage";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getHistoryItemStyle,
  getPromptContent,
  getPromptStyle,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { PromptDTO, PromptOwnerType } from "@shaarp/common";
import {
  ChevronDownIcon,
  HistoryIcon,
  Star,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

const PROMPT_HISTORY_KEY = "prompt-history";
const MAX_HISTORY_ITEMS = 5;

export type PromptHistoryItem = {
  id: string;
  title: string;
  content: string;
  usedAt: string;
  icon?: string;
  categoryId?: string;
  ownerType?: PromptOwnerType;
};

export function addPromptToHistory(
  prompt: PromptDTO,
  title?: string,
  lang: "fr" | "en" = "fr",
) {
  const stored = localStorage.getItem(PROMPT_HISTORY_KEY);
  const history: PromptHistoryItem[] = stored ? JSON.parse(stored) : [];
  const content = getPromptContent(prompt, lang);
  const promptTitle = getPromptTitle(prompt, lang);

  const newItem: PromptHistoryItem = {
    id: crypto.randomUUID(),
    title: title || promptTitle || content.slice(0, 50) + "...",
    content,
    usedAt: new Date().toISOString(),
    icon: prompt.icon,
    categoryId: prompt.categoryId,
    ownerType: prompt.ownerType,
  };

  // Remove duplicates (same content) and add new item at the beginning
  const filtered = history.filter((item) => item.content !== content);
  const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);

  localStorage.setItem(PROMPT_HISTORY_KEY, JSON.stringify(updated));
}

type PromptsPopoverProps = {
  onSelectPrompt: (prompt: PromptDTO) => void;
  onOpenLibrary: () => void;
  selectedPrompt?: PromptDTO | null;
  onRemovePrompt?: () => void;
  onViewPrompt?: () => void;
};

export default function PromptsPopover({
  onSelectPrompt,
  onOpenLibrary,
  selectedPrompt,
  onRemovePrompt,
  onViewPrompt,
}: PromptsPopoverProps) {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const lang = getSupportedLang(i18n.language);
  const [open, setOpen] = useState(false);
  const [history, setHistory, clearHistory] = useLocalStorage<
    PromptHistoryItem[]
  >(PROMPT_HISTORY_KEY, []);

  const { favoritePrompts, refreshFavorites } = useFavoritePrompts({
    fetchCustomPrompts: true,
    listenForChanges: true,
  });

  // Refresh data when popover opens
  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem(PROMPT_HISTORY_KEY);
      const freshHistory: PromptHistoryItem[] = stored
        ? JSON.parse(stored)
        : [];
      setHistory(freshHistory);

      refreshFavorites();
    }
  }, [open]);

  const hasHistory = history && history.length > 0;
  const hasFavorites = favoritePrompts.length > 0;

  const handleSelectHistoryItem = (item: PromptHistoryItem) => {
    onSelectPrompt({
      content: item.content,
      title: item.title,
      icon: item.icon,
      categoryId: item.categoryId,
      ownerType: item.ownerType,
    } as PromptDTO);
    setOpen(false);
  };

  const handleSelectFavorite = (prompt: PromptDTO) => {
    onSelectPrompt(prompt);
    addPromptToHistory(prompt, undefined, lang);
    setOpen(false);
  };

  const handleClearHistory = () => {
    clearHistory();
    setOpen(false);
  };

  // Selected prompt button: replaces the default trigger
  const selectedPromptStyle = selectedPrompt
    ? getPromptStyle(selectedPrompt)
    : null;
  const selectedPromptTitle = selectedPrompt
    ? getPromptTitle(selectedPrompt, lang)
    : null;
  const hasSelectedPrompt = !!(selectedPromptStyle && selectedPromptTitle);

  // On mobile, the tactique label competes for room with the rest of the
  // composer controls and hover-to-reveal the remove (X) is unavailable. Show a
  // compact colored badge: the tactique icon (tap to view) plus an always-visible
  // X to remove it.
  const selectedPromptButton = hasSelectedPrompt ? (
    isMobile ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(
          "h-9 gap-1.5 px-2.5 shadow-none",
          selectedPromptStyle.colors.border,
          selectedPromptStyle.colors.bg,
          selectedPromptStyle.colors.hoverBg,
        )}
        onClick={onViewPrompt}
        aria-label={selectedPromptTitle}
      >
        <selectedPromptStyle.Icon
          className={cn("size-3.5 shrink-0", selectedPromptStyle.colors.icon)}
        />
        <span
          role="button"
          tabIndex={0}
          aria-label={t("chat.input.prompts.removeSelected")}
          className="shrink-0 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onRemovePrompt?.();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              onRemovePrompt?.();
            }
          }}
        >
          <XIcon className="size-3.5" />
        </span>
      </Button>
    ) : (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(
          "group/prompt h-9 gap-1.5 max-w-[180px] shadow-none",
          selectedPromptStyle.colors.border,
          selectedPromptStyle.colors.bg,
          selectedPromptStyle.colors.hoverBg,
        )}
        onClick={onViewPrompt}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span
              role="button"
              tabIndex={0}
              className="shrink-0 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                onRemovePrompt?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemovePrompt?.();
                }
              }}
            >
              <selectedPromptStyle.Icon
                className={cn(
                  "size-3.5 group-hover/prompt:hidden",
                  selectedPromptStyle.colors.icon,
                )}
              />
              <XIcon className="size-3.5 hidden group-hover/prompt:block" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{t("chat.input.prompts.removeSelected")}</p>
          </TooltipContent>
        </Tooltip>
        <span className="truncate text-xs font-medium">
          {selectedPromptTitle}
        </span>
      </Button>
    )
  ) : null;

  // On mobile the composer is space-constrained, so the Tactiques control is a
  // single button with no chevron. It opens the full library, which already
  // surfaces favorites and pinned prompts.
  if (isMobile) {
    return (
      selectedPromptButton ?? <PromptTriggerButton onClick={onOpenLibrary} />
    );
  }

  // No history and no favorites: still expose the dropdown affordance so the
  // Tactiques control always reads as a dropdown. The chevron opens the library.
  if (!hasHistory && !hasFavorites) {
    if (selectedPromptButton) {
      return selectedPromptButton;
    }
    return (
      <ButtonGroup orientation="horizontal" className="h-9 w-fit">
        <PromptTriggerButton onClick={onOpenLibrary} />
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shadow-none"
              onClick={onOpenLibrary}
              aria-label={t("chat.input.prompts.select")}
            >
              <ChevronDownIcon className="size-4 opacity-50" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{t("chat.input.prompts.select")}</p>
          </TooltipContent>
        </Tooltip>
      </ButtonGroup>
    );
  }

  const promptsSections = (
    <>
      {/* Favorites Section */}
      {hasFavorites && (
        <>
          <div className="sticky top-0 z-10 bg-popover px-4 py-3 border-b border-transparent">
            <div className="flex items-center gap-2">
              <Star className="size-4 text-yellow-500 fill-yellow-500" />
              <h3 className="text-sm font-semibold">
                {t("chat.input.prompts.popover.favorites.title")}
              </h3>
            </div>
          </div>

          <div className="px-2 pb-2">
            <div className="space-y-0.5">
              {favoritePrompts.map((prompt) => (
                <PromptListItem
                  key={`favorite-${prompt.id}`}
                  prompt={prompt}
                  onClick={handleSelectFavorite}
                />
              ))}
            </div>
          </div>

          {hasHistory && <Separator />}
        </>
      )}

      {/* History Section */}
      {hasHistory && (
        <>
          <div className="sticky top-0 z-10 bg-popover px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <HistoryIcon className="size-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">
                  {t("chat.input.prompts.popover.history.title")}
                </h3>
              </div>
              <button
                onClick={handleClearHistory}
                className="p-1 rounded-md text-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                aria-label={t(
                  "chat.input.prompts.popover.history.clearHistory",
                )}
              >
                <Trash2Icon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="pb-2">
            <div className="space-y-0.5 px-2">
              {history.map((item) => {
                const { Icon, colors } = getHistoryItemStyle(
                  item.icon,
                  item.categoryId,
                  item.ownerType,
                );
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelectHistoryItem(item)}
                    className={cn(
                      "w-full flex items-start gap-3 px-2 py-2 rounded-md text-left",
                      "hover:bg-muted/60 transition-colors",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full shrink-0",
                        colors.iconBg,
                      )}
                    >
                      <Icon className={cn("w-4 h-4", colors.icon)} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm font-medium truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {item.content}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );

  // Button group: main action opens library (or shows selected prompt), chevron opens popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ButtonGroup orientation="horizontal" className="h-9 w-fit">
        {selectedPromptButton ?? <PromptTriggerButton onClick={onOpenLibrary} />}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shadow-none"
                aria-label={t("chat.input.prompts.historyAndFavorites")}
              >
                <ChevronDownIcon className="size-4 opacity-50" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{t("chat.input.prompts.historyAndFavorites")}</p>
          </TooltipContent>
        </Tooltip>
      </ButtonGroup>

      <PopoverContent
        className="w-[400px] p-0"
        align="center"
        side="bottom"
        sideOffset={8}
      >
        <div
          className={cn(
            "flex flex-col max-h-[420px] overflow-y-auto",
            "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
          )}
        >
          {promptsSections}
        </div>
      </PopoverContent>
    </Popover>
  );
}
